import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Eraser,
  Loader2,
  Mail,
  PenLine,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * ContractSignaturePanel
 * -----------------------
 * Reusable signing surface for STUDENT_COMMITMENT / ASSOCIATION_AGREEMENT.
 *
 * • Draws with PointerEvents (mouse/touch/pen), stores stroke coords normalized
 *   to [0..1] so the backend can re-render at any DPI.
 * • Requires typed_name to match `expectedName` (backend also enforces).
 * • Manages OTP challenge lifecycle: request → user types code → sign.
 * • Idempotency-Key: stable within one signing attempt; regenerated only after
 *   a successful 200 OR when the user explicitly resets/clears the attempt.
 *   Retries of the same submission reuse the same key (server dedupes).
 * • All errors surfaced via formatApiError → Arabic toast.
 */
export default function ContractSignaturePanel({
  documentId,
  expectedName,
  emailMasked,
  onSigned,
}) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]); // [[{x,y},...], ...]
  const currentStrokeRef = useRef(null);
  const drawingRef = useRef(false);
  const idemKeyRef = useRef(_newIdemKey());

  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [challenge, setChallenge] = useState(null); // { challenge_id, expires_in_seconds, email_masked }
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(null); // 'otp' | 'sign' | null
  const [dpr, setDpr] = useState(
    typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
  );

  // Resize canvas to CSS box × DPR for crisp lines.
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0f172a";
    setDpr(ratio);
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    // Clear in CSS space (matrix already scaled by DPR).
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0f172a";
    for (const stroke of strokesRef.current) {
      if (!stroke?.length) continue;
      ctx.beginPath();
      stroke.forEach((point, idx) => {
        const x = point.x * rect.width;
        const y = point.y * rect.height;
        if (idx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
    setHasInk(strokesRef.current.some((s) => s?.length));
  }, []);

  useEffect(() => {
    resizeCanvas();
    const handler = () => resizeCanvas();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [resizeCanvas]);

  const normalize = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    return {
      x: Math.max(0, Math.min(1, Number(x.toFixed(5)))),
      y: Math.max(0, Math.min(1, Number(y.toFixed(5)))),
    };
  };

  const handlePointerDown = (event) => {
    if (busy) return;
    event.preventDefault();
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    const point = normalize(event);
    currentStrokeRef.current = [point];
    strokesRef.current.push(currentStrokeRef.current);
    redraw();
  };

  const handlePointerMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const point = normalize(event);
    const stroke = currentStrokeRef.current;
    if (!stroke) return;
    const last = stroke[stroke.length - 1];
    if (last && Math.hypot(point.x - last.x, point.y - last.y) < 0.003) return;
    stroke.push(point);
    if (stroke.length > 2000) stroke.length = 2000;
    redraw();
  };

  const finishStroke = () => {
    drawingRef.current = false;
    currentStrokeRef.current = null;
    // Enforce hard cap: max 100 strokes (backend also enforces).
    if (strokesRef.current.length > 100) {
      strokesRef.current = strokesRef.current.slice(-100);
      redraw();
    }
  };

  const clearInk = () => {
    strokesRef.current = [];
    redraw();
    toast.info("مُسحَ التوقيع؛ ارسمه من جديد");
  };

  const requestOtp = async () => {
    if (busy) return;
    setBusy("otp");
    try {
      const { data } = await api.post(`/contracts/documents/${documentId}/otp`);
      setChallenge(data);
      setOtp("");
      toast.success(`أُرسل رمز OTP إلى ${data.email_masked}`);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(null);
    }
  };

  const performSign = async () => {
    if (busy) return;
    const cleanName = typedName.trim().replace(/\s+/g, " ");
    const expected = (expectedName || "").trim().replace(/\s+/g, " ");
    if (!cleanName || cleanName.toLocaleLowerCase("ar") !== expected.toLocaleLowerCase("ar")) {
      toast.error("الاسم المكتوب لا يطابق اسم الحساب");
      return;
    }
    if (!consent) {
      toast.error("يجب تأكيد الموافقة الصريحة");
      return;
    }
    if (!hasInk) {
      toast.error("ارسم توقيعك في المربّع قبل الإرسال");
      return;
    }
    if (!challenge?.challenge_id) {
      toast.error("اطلب رمز OTP أولًا");
      return;
    }
    if (!/^[0-9]{6}$/.test(otp)) {
      toast.error("رمز OTP يجب أن يكون 6 أرقام");
      return;
    }

    setBusy("sign");
    try {
      const { data } = await api.post(
        `/contracts/documents/${documentId}/sign`,
        {
          consent: true,
          typed_name: cleanName,
          strokes: strokesRef.current,
          challenge_id: challenge.challenge_id,
          otp,
        },
        { headers: { "Idempotency-Key": idemKeyRef.current } },
      );
      toast.success("تم توثيق التوقيع");
      // Regenerate key ONLY after success so the next signing attempt has a
      // fresh idempotency scope.
      idemKeyRef.current = _newIdemKey();
      setChallenge(null);
      setOtp("");
      setConsent(false);
      setTypedName("");
      strokesRef.current = [];
      redraw();
      if (typeof onSigned === "function") onSigned(data);
    } catch (error) {
      // Retries of the same failed request keep the same key on purpose.
      toast.error(formatApiError(error));
    } finally {
      setBusy(null);
    }
  };

  const otpTtlMin = useMemo(() => {
    if (!challenge?.expires_in_seconds) return null;
    return Math.round(challenge.expires_in_seconds / 60);
  }, [challenge]);

  return (
    <section
      className="rounded-2xl border border-teal-200 bg-white p-5 shadow-sm"
      data-testid="contract-signature-panel"
      dir="rtl"
    >
      <header className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-teal-50 text-teal-700">
          <PenLine className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-slate-900">
            توقيع الوثيقة
          </h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            اكتب اسمك بالضبط كما هو مسجّل في حسابك، ثم ارسم توقيعك واطلب رمز
            التحقق قبل الإرسال.
          </p>
        </div>
      </header>

      <label className="mt-5 block text-sm font-semibold text-slate-700">
        الاسم كما هو مسجّل في الحساب
        <input
          type="text"
          value={typedName}
          onChange={(event) => setTypedName(event.target.value)}
          placeholder={expectedName || ""}
          className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
          data-testid="signature-typed-name"
        />
        {expectedName && (
          <span className="mt-1 block text-xs text-slate-400">
            سيُقارَن التطابق دون حساسية لحالة الأحرف والمسافات.
          </span>
        )}
      </label>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">
            ارسم توقيعك داخل المربّع
          </div>
          <button
            type="button"
            onClick={clearInk}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-slate-300 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="signature-clear"
          >
            <Eraser className="h-3.5 w-3.5" />
            مسح
          </button>
        </div>
        <div
          className="mt-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-1"
          style={{ touchAction: "none" }}
        >
          <canvas
            ref={canvasRef}
            className="block h-40 w-full cursor-crosshair rounded-xl bg-white"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
            onPointerLeave={finishStroke}
            data-testid="signature-canvas"
            data-dpr={dpr}
          />
        </div>
        {!hasInk && (
          <div className="mt-2 text-xs text-slate-400">
            يدعم اللمس والقلم والفأرة. لا تقلق — الإحداثيات فقط ما تُرسَل، لا صورة.
          </div>
        )}
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
          className="mt-1 h-5 w-5 rounded border-slate-300 text-teal-700 focus:ring-teal-500"
          data-testid="signature-consent"
        />
        <span>
          أُقرّ بأنني قرأت بنود الوثيقة كاملة وأوافق على التوقيع الإلكتروني لها، وأن
          هذا التوقيع مُلزِم قانونيًا وفق نظام التعاملات الإلكترونية.
        </span>
      </label>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <ShieldCheck className="h-4 w-4 text-teal-700" />
          التحقق برمز OTP
        </div>
        <p className="mt-1 text-xs leading-6 text-slate-500">
          سيصلك رمز مكوّن من 6 أرقام على بريدك المسجّل
          {emailMasked ? ` (${emailMasked})` : ""} صالح لعدة دقائق.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={requestOtp}
            disabled={Boolean(busy)}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
            data-testid="signature-request-otp"
          >
            {busy === "otp" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            {challenge ? "إعادة إرسال الرمز" : "إرسال رمز OTP"}
          </button>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otp}
            onChange={(event) =>
              setOtp(event.target.value.replace(/[^0-9]/g, "").slice(0, 6))
            }
            placeholder="000000"
            dir="ltr"
            className="min-h-11 w-36 rounded-xl border border-slate-300 px-3 text-center font-mono text-lg tracking-[0.4em] outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
            data-testid="signature-otp"
          />
          {otpTtlMin != null && (
            <span className="text-xs text-slate-500">
              يصل خلال ثوانٍ • صالح لحوالي {otpTtlMin} دقيقة
            </span>
          )}
        </div>
      </div>

      {!consent && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          يجب تأكيد الموافقة الصريحة قبل إتمام التوقيع.
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-400" data-testid="signature-idem">
          مفتاح تكرار الطلب مُثبَّت لهذه المحاولة — إعادة الإرسال بعد فشل شبكي لا
          تُنشئ توقيعًا مكررًا.
        </div>
        <button
          type="button"
          onClick={performSign}
          disabled={Boolean(busy) || !challenge || !consent}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-teal-700 px-5 text-sm font-bold text-white hover:bg-teal-600 disabled:opacity-50"
          data-testid="signature-submit"
        >
          {busy === "sign" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          إتمام التوقيع
        </button>
      </div>
    </section>
  );
}

function _newIdemKey() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {
    /* fall through */
  }
  return `sig-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
